import Dexie from 'dexie';

export type TabloProjectHistoryEntry = {
  tabloProjectId: string;
  lastHistoryEntryId?: string | null;
  fromHistory?: boolean;
};

export interface TabloProjectHistoryStorage<
  T extends TabloProjectHistoryEntry,
> {
  transaction<TResult>(operation: () => Promise<TResult>): Promise<TResult>;
  put(entry: T): Promise<void>;
  primaryKeys(tabloProjectId: string): Promise<string[]>;
  bulkDelete(keys: string[]): Promise<void>;
  get(key: string): Promise<T | undefined>;
}

const database = new Dexie('zalamo-tablo-project4');
database.version(3).stores({
  historyEntries: '&lastHistoryEntryId,tabloProjectId',
});

const createDexieHistoryStorage = <
  T extends TabloProjectHistoryEntry,
>(): TabloProjectHistoryStorage<T> => {
  const table = database.table<T, string>('historyEntries');

  return {
    transaction: (operation) => database.transaction('rw', table, operation),
    put: async (entry) => {
      await table.put(entry);
    },
    primaryKeys: (tabloProjectId) =>
      table.where('tabloProjectId').equals(tabloProjectId).primaryKeys(),
    bulkDelete: (keys) => table.bulkDelete(keys),
    get: (key) => table.get(key),
  };
};

const MAX_HISTORY_ENTRIES = 100;

export class TabloProjectHistory<T extends TabloProjectHistoryEntry> {
  private pendingOperation: Promise<void> = Promise.resolve();

  constructor(
    private readonly storage: TabloProjectHistoryStorage<T> = createDexieHistoryStorage<T>(),
  ) {}

  record(entry: T): Promise<void> {
    if (!entry.lastHistoryEntryId) {
      return Promise.resolve();
    }

    return this.enqueue(() =>
      this.storage.transaction(async () => {
        await this.recordInTransaction(entry);
      }),
    );
  }

  getPrevious(entry: T): Promise<T | null> {
    return this.getAdjacent(entry, -1);
  }

  getNext(entry: T): Promise<T | null> {
    return this.getAdjacent(entry, 1);
  }

  private getAdjacent(entry: T, direction: -1 | 1): Promise<T | null> {
    const historyEntryId = entry.lastHistoryEntryId;
    if (!historyEntryId) {
      return Promise.resolve(null);
    }

    return this.enqueue(() =>
      this.storage.transaction(async () => {
        // Flush the current edit even when its debounced autosave has not run.
        const keys = await this.recordInTransaction(entry);
        const currentIndex = keys.indexOf(historyEntryId);
        const adjacentKey =
          currentIndex < 0 ? undefined : keys[currentIndex + direction];

        return adjacentKey
          ? (await this.storage.get(adjacentKey)) ?? null
          : null;
      }),
    );
  }

  private async recordInTransaction(entry: T): Promise<string[]> {
    const snapshot = { ...entry };
    // Navigation is transient UI state, never a reason to skip a new revision.
    delete snapshot.fromHistory;
    await this.storage.put(snapshot);

    const keys = await this.storage.primaryKeys(entry.tabloProjectId);
    keys.sort((left, right) => left.localeCompare(right));
    const obsoleteCount = Math.max(0, keys.length - MAX_HISTORY_ENTRIES);
    if (obsoleteCount > 0) {
      await this.storage.bulkDelete(keys.slice(0, obsoleteCount));
    }

    return keys.slice(obsoleteCount);
  }

  private enqueue<TResult>(
    operation: () => Promise<TResult>,
  ): Promise<TResult> {
    const result = this.pendingOperation.then(operation);
    // A failed write reaches its caller without disabling subsequent edits.
    this.pendingOperation = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }
}
